import React from 'react';
import { LinkInterface } from '@/types';
import { bgColorClassname } from '@/utils';
import { Heading, FeaturedProgram } from '@/components';
import type { ImageAsset, PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

interface ImageWithMask {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
  mask?: string;
}

interface FeaturedEntry {
  _id: string;
  _key: string;
  _type: string;
  slug?: {
    current: string;
  };
  title?: string;
  description?: string;
  blurb?: PortableTextBlockType;
  featuredImage?: ImageWithMask;
  content?: PortableTextBlockType;
  carouselImage?: ImageWithMask;
  language: string;
}

interface EntryCarouselProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  layoutWidth?: string;
  title: string;
  includeCta: boolean;
  cta?: LinkInterface;
  featuredEntries: FeaturedEntry[];
}

const getItemClass = (itemCount: number): string => {
  switch (itemCount) {
    case 2:
      return 'basis-3/5 md:basis-1/2';
    case 3:
      return 'basis-3/5 md:basis-1/3';
    case 4:
      return 'basis-3/5 md:basis-1/3 lg:basis-1/4';
    default:
      return 'basis-3/5 md:basis-1/3 lg:basis-1/4 xl:basis-1/5';
  }
};

const getItemClassTwoHalf = (itemCount: number): string => {
  switch (itemCount) {
    case 2:
      return 'basis-1/2';
    default:
      return 'basis-3/5 md:basis-1/2';
  }
};

const getItemClassTwoThird = (itemCount: number): string => {
  switch (itemCount) {
    case 2:
      return 'basis-1/2';
    default:
      return 'basis-3/5 md:basis-1/2 xl:basis-1/3';
  }
};

const getItemClassOneThird = (itemCount: number): string => {
  switch (itemCount) {
    case 2:
      return 'basis-1/2 md:basis-full';
    default:
      return 'basis-3/5 md:basis-full';
  }
};

const navigationClass = (itemCount: number, layoutWidth?: string): string => {
  const visibilityClasses: Record<string, Record<number, string | undefined>> = {
    '1/3': {
      2: 'hidden md:flex',
    },
    '1/2': {
      2: 'hidden',
    },
    '2/3': {
      2: 'hidden',
      3: 'xl:hidden',
    },
  };

  if (!layoutWidth && itemCount <= 5) {
    return 'flex xl:hidden';
  }

  const layoutClasses = visibilityClasses[layoutWidth || '2/3'];

  // Return specific class for the itemCount if available, otherwise return ''
  return layoutClasses?.[itemCount] || '';
};

const getDynamicClass = (layoutWidth: string | undefined, itemCount: number): string => {
  switch (layoutWidth) {
    case '2/3':
      return getItemClassTwoThird(itemCount);
    case '1/2':
      return getItemClassTwoHalf(itemCount);
    case '1/3':
      return getItemClassOneThird(itemCount);
    default:
      return getItemClass(itemCount);
  }
};

const EntryCarousel: React.FC<EntryCarouselProps> = ({
  backgroundColor,
  title,
  includeCta,
  cta,
  featuredEntries,
  layoutWidth,
}) => {
  const bgColor = backgroundColor ? backgroundColor.value : '';
  const isBgColorYellow = ['#FFE23D', '#FFD700'].includes(bgColor || '');

  const sliderArrowClass = `relative flex top-0 h-7 w-7 md:h-9 md:w-9 translate-x-0 translate-y-0 ${isBgColorYellow ? 'bg-white' : 'bg-yellow-500'} border-0 ease-bounce hover:bg-blue-950 focus:bg-blue-950 active:bg-blue-950 hover:text-white focus:text-white active:text-white ${navigationClass(featuredEntries.length, layoutWidth)}`;

  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='mx-auto flex max-w-screen-xl flex-col gap-6 overflow-hidden px-5 py-8 md:p-[50px]'>
        <Heading
          title={title}
          isShowButton={includeCta}
          cta={cta}
        />
        {featuredEntries && featuredEntries.length > 0 && (
          <div className='flex gap-2'>
            <Carousel
              opts={{ align: 'start' }}
              className='w-full pr-4 xl:pr-0'
            >
              <CarouselContent className='-xl:ml-4 -mx-4 xl:mr-0'>
                {featuredEntries.map((entry: FeaturedEntry, index) => {
                  const hasDescription = entry.blurb || entry.content;
                  const titleMargin = hasDescription ? 'sm:mb-1.5' : 'sm:mb-3';
                  const maskType = entry?.carouselImage?.mask || 'square';

                  return (
                    <CarouselItem
                      key={`${index}-${entry._key || entry._id}`}
                      className={getDynamicClass(layoutWidth, featuredEntries.length)}
                    >
                      <FeaturedProgram
                        id={index}
                        entry={entry}
                        titleClassName={`text-base sm:text-2xl font-bold mb-1 ${titleMargin}`}
                        descriptionClassName='font-light leading-[20px]'
                        imageRounded={true}
                        showTimeLocation={false}
                        maskType={maskType}
                      />
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <div className='-mr-4 mt-6 flex justify-end gap-8 xl:mr-0'>
                <CarouselPrevious className={`left-0 ${sliderArrowClass}`} />
                <CarouselNext className={`right-0 ${sliderArrowClass}`} />
              </div>
            </Carousel>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntryCarousel;
